import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, ShieldCheck, ShieldAlert, ShieldX, Clock,
  CheckCircle2, XCircle, Camera, Video, ShoppingBag, Hammer,
} from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const SECTION_THEME = {
  probound: { name: 'ProBound', primary: '#2F5E96', secondary: '#1f4068' },
  proepoxy: { name: 'ProEpoxy', primary: '#7A0B7A', secondary: '#5a085a' },
  prodecor: { name: 'ProDecor', primary: '#D4A24B', secondary: '#a87f30' },
};

const STATUS_META = {
  approved: { label: 'Approved', icon: ShieldCheck, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  at_risk: { label: 'At Risk', icon: ShieldAlert, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  eligible_pending: { label: 'Awaiting Approval', icon: Clock, color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  not_eligible: { label: 'Not Yet Eligible', icon: ShieldX, color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' },
  suspended: { label: 'Suspended', icon: ShieldX, color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
};

const CriteriaRow = ({ ok, label, detail }) => (
  <div className="flex items-start gap-2 py-2">
    {ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-gray-300 flex-shrink-0" />}
    <div className="flex-1">
      <div className={`text-sm font-medium ${ok ? 'text-emerald-900' : 'text-gray-700'}`}>{label}</div>
      {detail && <div className="text-xs text-gray-500 mt-0.5">{detail}</div>}
    </div>
  </div>
);

const SectionCard = ({ cert, config, onApply }) => {
  const theme = SECTION_THEME[cert.section] || { name: cert.section, primary: '#333', secondary: '#111' };
  const meta = STATUS_META[cert.status] || STATUS_META.not_eligible;
  const Icon = meta.icon;
  const ev = cert.evidence || {};
  const labels = (config && config.photo_category_labels) || {};

  const proj = ev.latest_completed_project;
  const photosByCat = proj?.photos_by_category || {};
  const requiredCats = ev.required_photo_categories || [];

  // ProBound has a portfolio submission flow (sub-categories). Other sections auto-qualify on criteria.
  const canSubmitPortfolio = cert.section === 'probound';

  return (
    <Card data-testid={`cert-section-${cert.section}`} className="overflow-hidden">
      <div
        className="relative p-5 text-white"
        style={{
          background: `linear-gradient(135deg, #0B0B0E 0%, ${theme.primary} 60%, ${theme.secondary} 100%)`,
          borderTop: '3px solid #D4A24B',
          borderBottom: '1px solid #D4A24B',
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-full shadow-inner"
              style={{ background: '#0B0B0E', border: '2px solid #D4A24B' }}
            >
              <ShieldCheck className="w-5 h-5" style={{ color: '#D4A24B' }} />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#D4A24B' }}>Ox Approved · {theme.name}</div>
              <div className="text-xl md:text-2xl font-bold">Approved Installer</div>
            </div>
          </div>
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white/90 ${meta.color}`}>
            <Icon className="w-3.5 h-3.5" />
            {meta.label}
          </div>
        </div>
      </div>

      <div className={`p-5 border-l-4 ${meta.border}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <CriteriaRow
            ok={ev.completed_projects > 0}
            label={<><Hammer className="inline w-4 h-4 mr-1" /> ≥1 completed project (last 30 days)</>}
            detail={proj ? `Latest: ${proj.reference || proj.customer_name || 'Project'}` : 'Mark a project Completed to qualify.'}
          />
          <CriteriaRow
            ok={!!(proj && proj.all_categories_covered)}
            label={<><Camera className="inline w-4 h-4 mr-1" /> Photos in every required category</>}
            detail={
              proj
                ? requiredCats.map(c => `${labels[c] || c}: ${photosByCat[c] || 0}`).join(' · ')
                : 'Upload progress photos on a completed project.'
            }
          />
          <CriteriaRow
            ok={ev.videos_all_watched}
            label={<><Video className="inline w-4 h-4 mr-1" /> All training videos watched</>}
            detail={`${ev.videos_watched || 0} / ${ev.videos_total || 0} watched`}
          />
          <CriteriaRow
            ok={ev.orders_in_window > 0}
            label={<><ShoppingBag className="inline w-4 h-4 mr-1" /> Ordered through the app (last 30 days)</>}
            detail={`${ev.orders_in_window || 0} order${ev.orders_in_window === 1 ? '' : 's'} in window`}
          />
        </div>

        {cert.status === 'eligible_pending' && (
          <div className={`mt-4 p-3 rounded-lg ${meta.bg} text-sm ${meta.color}`}>
            All criteria met — your application is in the admin review queue.
          </div>
        )}
        {cert.status === 'approved' && (
          <div className={`mt-4 p-3 rounded-lg ${meta.bg} text-sm ${meta.color}`}>
            You&apos;re an Ox Approved {theme.name} Installer. Keep all criteria green to stay approved each month.
          </div>
        )}
        {cert.status === 'at_risk' && (
          <div className={`mt-4 p-3 rounded-lg ${meta.bg} text-sm ${meta.color}`}>
            One or more criteria slipped this month. Restore them to keep your badge — you have 30 days from when you fell out of compliance.
          </div>
        )}
        {cert.status === 'suspended' && (
          <div className={`mt-4 p-3 rounded-lg ${meta.bg} text-sm ${meta.color}`}>
            Approval suspended. Meet all criteria again to re-apply.
          </div>
        )}

        {canSubmitPortfolio && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              data-testid={`apply-${cert.section}`}
              onClick={() => onApply(cert.section)}
              className="w-full text-white font-semibold"
              style={{ background: theme.primary }}
            >
              <Hammer className="w-4 h-4 mr-2" />
              {['approved', 'at_risk'].includes(cert.status) ? `Add to my ${theme.name} portfolio` : `Submit / Update Portfolio for ${theme.name}`}
            </Button>
            <p className="text-xs text-gray-500 mt-2 text-center">
              {['approved', 'at_risk'].includes(cert.status) ? 'Keep adding finished projects — Ox Resin see every one.' : 'ProBound has three sub-categories. Submit a portfolio entry per category to be reviewed by Ox Resin.'}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default function MyCertification() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sectionFilter = searchParams.get('section');
  useContext(AuthContext);
  const [certs, setCerts] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/certifications/me`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/certifications/config`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
    ]).then(([c, cfg]) => {
      setCerts(c);
      setConfig(cfg);
      setLoading(false);
    });
  }, []);

  // When entering from a section hub, filter the list to just that section
  const visibleCerts = sectionFilter ? certs.filter(c => c.section === sectionFilter) : certs;
  const sectionTheme = sectionFilter ? SECTION_THEME[sectionFilter] : null;
  const titleSuffix = sectionTheme ? sectionTheme.name : 'Ox';
  const headerStyle = sectionTheme
    ? {
        background: `linear-gradient(135deg, #0B0B0E 0%, ${sectionTheme.primary} 60%, ${sectionTheme.secondary} 100%)`,
        borderBottom: '2px solid #D4A24B',
      }
    : { background: 'linear-gradient(135deg, #111, #2a0f2a)', borderBottom: '2px solid #D4A24B' };

  // ProBound is the only section with a portfolio submission form today
  const handleApply = (section) => navigate(`/approval/${section}`);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={headerStyle}>
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center gap-4 flex-wrap">
          <button onClick={() => navigate(-1)} data-testid="cert-back" className="p-2 hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              <ShieldCheck className="w-6 h-6" /> My {titleSuffix} Approved Status
            </h1>
            <p className="text-white/70 text-sm">
              {sectionFilter
                ? `Track your ${titleSuffix} approval criteria below. Keep them green to stay approved each month.`
                : 'Maintain these criteria each month to stay approved across your sections.'}
            </p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
            <Home className="w-4 h-4 mr-1" /> Home
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : visibleCerts.length === 0 ? (
          <Card className="p-10 text-center text-gray-500">
            {sectionFilter
              ? `No ${titleSuffix} certification yet — complete a project, upload progress photos and watch all training videos to qualify.`
              : 'No certifications yet.'}
            {sectionFilter === 'probound' && (
              <div className="mt-4">
                <Button data-testid="apply-probound-empty" onClick={() => handleApply('probound')} className="bg-[#2F5E96] hover:bg-[#1f4068] text-white">
                  Submit Portfolio
                </Button>
              </div>
            )}
          </Card>
        ) : (
          visibleCerts.map(c => <SectionCard key={c.section} cert={c} config={config} onApply={handleApply} />)
        )}
      </div>
    </div>
  );
}
